import {
  ArrowRight,
  CheckCircle2,
  Lock,
  RefreshCw,
  XCircle,
} from "lucide-react";

import { ModuleRow, StatCard, categoryMeta } from "../ScannerParts";
import type { ScannerController } from "../useScannerController";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface CompletePhaseProps {
  c: ScannerController;
  onClose: () => void;
  primaryAction?: { label: string; onClick: () => void };
}

export default function CompletePhase({
  c,
  onClose,
  primaryAction,
}: CompletePhaseProps) {
  if (c.isFailed) return <FailedState c={c} onClose={onClose} />;

  const syncedAt = c.lastSyncedAt
    ? new Date(c.lastSyncedAt).toLocaleString()
    : "just now";

  return (
    <div className="space-y-4 p-5">
      {/* Hero */}
      <Card className="shadow-none relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-success/10 via-info/5 to-transparent" />
        <div className="relative flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-[11px] font-medium text-success">
              <CheckCircle2 className="h-3.5 w-3.5" /> Scan complete
              {c.isPartial ? " (partial)" : ""}
            </span>
            <h2 className="text-[19px] font-semibold tracking-tight">
              Application analyzed
            </h2>
            <p className="mt-1 truncate text-[13px] text-muted-foreground">
              Learned{" "}
              <span className="font-mono text-foreground">{c.displayHost}</span>{" "}
              · synced {syncedAt}
            </p>
            {c.isPartial && (
              <p className="mt-1.5 text-[12px] text-warning">
                The last crawl was interrupted, so some pages may be missing.
                Rescan to refresh.
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={c.goToConnect}
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Rescan
            </Button>
            {primaryAction ? (
              <Button
                size="sm"
                onClick={primaryAction.onClick}
                className="gap-1.5"
              >
                {primaryAction.label} <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Pages" value={c.pageCount} />
        <StatCard label="Forms" value={c.counters.forms} />
        <StatCard label="Modules" value={c.counters.modules} />
        <StatCard label="Protected" value={c.counters.protectedRoutes} />
        <StatCard label="Public" value={c.counters.publicRoutes} />
      </div>

      {/* Results */}
      <Tabs defaultValue="modules" className="space-y-3">
        <TabsList>
          <TabsTrigger value="modules">Detected modules</TabsTrigger>
          <TabsTrigger value="pages">Discovered pages</TabsTrigger>
        </TabsList>

        <TabsContent value="modules">
          {c.modules.length === 0 ? (
            <EmptyResults text="No modules were detected." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {c.modules.map((m) => (
                <ModuleRow key={m.name} module={m} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pages">
          {c.discoveries.length === 0 ? (
            <EmptyResults text="No pages were discovered." />
          ) : (
            <Card className="shadow-none">
              <ScrollArea className="h-[320px]">
                <ul className="divide-y divide-border">
                  {c.discoveries.map((d) => {
                    const meta = categoryMeta(d.category);
                    const Icon = meta.icon;
                    return (
                      <li
                        key={d.id}
                        className="flex items-center gap-3 px-4 py-2.5"
                      >
                        <span
                          className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border ${meta.tone}`}
                        >
                          <Icon className="h-3 w-3" />
                        </span>
                        <span
                          className="min-w-0 flex-1 truncate font-mono text-[12px] text-foreground"
                          title={d.routePath}
                        >
                          {d.routePath}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {meta.label}
                        </span>
                        {d.isAuthProtected && (
                          <Lock className="h-3 w-3 shrink-0 text-warning" />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FailedState({
  c,
  onClose,
}: {
  c: ScannerController;
  onClose: () => void;
}) {
  return (
    <div className="mx-auto max-w-lg p-5">
      <Card className="shadow-none overflow-hidden">
        <div className="border-b border-border bg-gradient-to-br from-destructive/10 via-transparent to-transparent p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-destructive/15 text-destructive">
              <XCircle className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold">
                Scan could not complete
              </h2>
              <p className="text-[12px] text-muted-foreground">
                {c.errorMessage ||
                  "The crawl failed. Check the URL and credentials, then try again."}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" onClick={c.goToConnect} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </Button>
        </div>
      </Card>
    </div>
  );
}

function EmptyResults({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">
      {text}
    </div>
  );
}
